import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, Radio } from '@mui/material';
import { CloseOutlinedIcon } from '../../../../assets/mui';
import { SipFrequency } from './SipOptions';
import { NetworkManager } from '../../../../services';
import toast from 'react-hot-toast';

interface StopSipPeriodModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (period: '7' | '10' | '15' | '1' | '2' | '3' | 'PERMANENT') => void;
  frequency: SipFrequency;
  subscriptionId?: string;
  refetchSubscriptions?: () => void;
}
interface SipPeriod {
  value: '7' | '10' | '15' | '1' | '2' | '3' | 'PERMANENT';
  label: string;
  restartDate: string | null;
  pauseTill: number | null;
}

const StopSipPeriodModal: React.FC<StopSipPeriodModalProps> = ({ open, onClose, onConfirm, subscriptionId, frequency, refetchSubscriptions }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'7' | '10' | '15' | '1' | '2' | '3' | 'PERMANENT' | null>(
    null,
  );
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [isPausingSubscription, setIsPausingSubscription] = useState(false);

  const calculateRestartDate = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}'${year.toString().slice(-2)}`;
  };

  const calculateRestartDateWeeks = (weeks: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + (weeks * 7));
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}'${year.toString().slice(-2)}`;
  };

  const calculateRestartDateMonths = (months: number): string => {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}'${year.toString().slice(-2)}`;
  };

const periods = useMemo((): SipPeriod[] => {
  const isPermanent: SipPeriod = { 
    value: 'PERMANENT', 
    label: 'Close Permanently', 
    restartDate: null, 
    pauseTill: null 
  };

  switch (frequency) {
    case 'DAILY':
      return [
        { value: '7', label: 'For 7 days', restartDate: calculateRestartDate(7), pauseTill: 7 },
        { value: '10', label: 'For 10 days', restartDate: calculateRestartDate(10), pauseTill: 10 },
        { value: '15', label: 'For 15 days', restartDate: calculateRestartDate(15), pauseTill: 15 },
        isPermanent,
      ];
    case 'WEEKLY':
      return [
        { value: '1', label: 'For 1 week', restartDate: calculateRestartDateWeeks(1), pauseTill: 1 },
        { value: '2', label: 'For 2 weeks', restartDate: calculateRestartDateWeeks(2), pauseTill: 2 },
        { value: '3', label: 'For 3 weeks', restartDate: calculateRestartDateWeeks(3), pauseTill: 3 },
        isPermanent,
      ];
    case 'MONTHLY':
      return [
        { value: '1', label: 'For 1 month', restartDate: calculateRestartDateMonths(1), pauseTill: 1 },
        { value: '2', label: 'For 2 months', restartDate: calculateRestartDateMonths(2), pauseTill: 2 },
        { value: '3', label: 'For 3 months', restartDate: calculateRestartDateMonths(3), pauseTill: 3 },
        isPermanent,
      ];
    default:
      // Fallback/Default case
      return [
        { value: '7', label: 'For 7 days', restartDate: calculateRestartDate(7), pauseTill: 1 },
        { value: '10', label: 'For 10 days', restartDate: calculateRestartDate(10), pauseTill: 2 },
        { value: '15', label: 'For 15 days', restartDate: calculateRestartDate(15), pauseTill: 3 },
        isPermanent,
      ];
  }
}, [frequency]);

  const handleConfirm = async () => {
    if (!selectedPeriod) {
      return;
    }

    if (selectedPeriod === 'PERMANENT') {
      if (!subscriptionId) {
        toast.error('Subscription ID not found. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
        return;
      }

      setIsCancellingSubscription(true);
      try {
        const path = NetworkManager.PATH.cancelSipSubscription.replace('{{subscriptionId}}', subscriptionId);
        await NetworkManager.Call({
          method: 'post',
          path: path,
        });
        
        toast.success('SIP cancelled permanently', {
          duration: 2000,
          position: 'top-center',
        });
        // Refetch subscription status after successful cancellation
        if (refetchSubscriptions) {
          refetchSubscriptions();
        }
        onConfirm(selectedPeriod);
        handleClose();
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Failed to cancel SIP. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
      } finally {
        setIsCancellingSubscription(false);
      }
    } else {
      // For temporary pauses (7, 10, 15 days), call pause API
      if (!subscriptionId) {
        toast.error('Subscription ID not found. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
        return;
      }

      setIsPausingSubscription(true);
      try {
        const path = NetworkManager.PATH.pauseSipSubscription.replace('{{subscriptionId}}', subscriptionId);
        const selectedPeriodObj = periods.find((p) => p.value === selectedPeriod);
        const pauseTillValue = selectedPeriodObj?.pauseTill;
        
        if (pauseTillValue === undefined || pauseTillValue === null) {
          toast.error('Invalid pause period selected', {
            duration: 2000,
            position: 'top-center',
          });
          setIsPausingSubscription(false);
          return;
        }

        await NetworkManager.Call({
          method: 'post',
          path: path,
          data: {
            pauseTill: pauseTillValue,
          },
        });
        
        // Dynamic success message based on frequency
        let successMessage = '';
        if (frequency === 'DAILY') {
          successMessage = `SIP paused for ${selectedPeriod} days`;
        } else if (frequency === 'WEEKLY') {
          successMessage = `SIP paused for ${selectedPeriod} ${selectedPeriod === '1' ? 'week' : 'weeks'}`;
        } else if (frequency === 'MONTHLY') {
          successMessage = `SIP paused for ${selectedPeriod} ${selectedPeriod === '1' ? 'month' : 'months'}`;
        } else {
          successMessage = `SIP paused for ${selectedPeriod} days`;
        }
        
        toast.success(successMessage, {
          duration: 2000,
          position: 'top-center',
        });
        // Refetch subscription status after successful pause
        if (refetchSubscriptions) {
          refetchSubscriptions();
        }
        onConfirm(selectedPeriod);
        handleClose();
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Failed to pause SIP. Please try again.', {
          duration: 2000,
          position: 'top-center',
        });
      } finally {
        setIsPausingSubscription(false);
      }
    }
  };

  const handleClose = () => {
    setSelectedPeriod(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: '680px',
          maxWidth: '680px',
        },
      }}
    >
      <DialogTitle sx={{ padding: '24px', paddingBottom: 0 }}>
        <div className="flex flex-row items-center gap-1.5 w-full">
          <div className="flex flex-col justify-center items-start flex-1">
            <h3
              className="text-[20px] font-semibold leading-[150%] flex items-center"
              style={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '150%',
                letterSpacing: '-0.02em',
                color: '#152420',
                width: '318px',
                height: '30px',
              }}
            >
              By what period you want to close?
            </h3>
            <p
              className="text-xs leading-[150%] mt-0"
              style={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '150%',
                color: '#737373',
                opacity: 0.7,
                width: '594px',
                height: '18px',
              }}
            >
              The plan will auto resume after the selected date or tenure
            </p>
          </div>
          <div className="flex-none" style={{ width: '32px', height: '32px' }}>
            <CloseOutlinedIcon
              className="cursor-pointer"
              onClick={handleClose}
              style={{
                width: '32px',
                height: '32px',
                color: '#71717A',
              }}
            />
          </div>
        </div>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '12px 20px 8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          className="flex flex-col justify-center items-center"
          style={{
            padding: '8px 0px 14px',
            gap: '16px',
            width: '640px',
          }}
        >
          <div
            className="flex flex-col justify-center items-center"
            style={{
              gap: '16px',
              width: '640px',
            }}
          >
            {periods.map((period) => (
              <div
                key={period.value}
                className="flex flex-row justify-between items-center cursor-pointer"
                onClick={() => setSelectedPeriod(period.value)}
                style={{
                  padding: '16px 11px',
                  gap: '8px',
                  width: '640px',
                  height: '53px',
                  background: '#FFFFFF',
                  border: '0.5px solid #E5E5E5',
                  borderRadius: '6.2px',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  className="flex flex-row items-center"
                  style={{
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '100px',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '8px',
                      }}
                    >
                      <Radio
                        checked={selectedPeriod === period.value}
                        onChange={() => setSelectedPeriod(period.value)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: '24px',
                          height: '24px',
                          '& .MuiSvgIcon-root': {
                            width: '24px',
                            height: '24px',
                          },
                        }}
                      />
                    </div>
                  </div>
                  <div
                    className="flex flex-col items-start"
                    style={{
                      padding: '4px 0px',
                      gap: '4.13px',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Inter',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '150%',
                        color: '#171717',
                        height: '21px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {period.label}
                    </span>
                  </div>
                </div>
                {period.restartDate && (
                  <div
                    className="flex flex-col items-start"
                    style={{
                      padding: '4px 0px',
                      gap: '4.13px',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Inter',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '150%',
                        color: '#525252',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Restarts on {period.restartDate}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
      <div
        className="flex flex-row items-center"
        style={{
          padding: '20px 16px 36px',
          gap: '12px',
          width: '680px',
        }}
      >
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!selectedPeriod || isCancellingSubscription || isPausingSubscription}
          style={{
            width: '648px',
            height: '57px',
            background: selectedPeriod ? '#171717' : '#171717',
            opacity: selectedPeriod && !isCancellingSubscription && !isPausingSubscription ? 1 : 0.3,
            borderRadius: '12px',
            border: 'none',
            cursor: selectedPeriod && !isCancellingSubscription && !isPausingSubscription ? 'pointer' : 'not-allowed',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 24px',
            gap: '10px',
          }}
        >
          <span
            style={{
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '17px',
              textAlign: 'center',
              color: '#FFFFFF',
              width: '81px',
              height: '17px',
            }}
          >
            {isCancellingSubscription ? 'Cancelling...' : isPausingSubscription ? 'Pausing...' : 'Stop Saving'}
          </span>
        </button>
      </div>
    </Dialog>
  );
};

export default StopSipPeriodModal;
